import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import type { OrgResponse } from "@bw/shared/types/generated";

import { PlatformPage } from "@/components/PlatformPage";
import { getContractTerms } from "@/shared/api";

import {
  approveOrg,
  getOrg,
  listOnboardingOrgs,
  rejectOrg,
  startReview,
} from "../api";
import {
  APPROVAL_PAGE_SIZE,
  EMPTY_APPROVAL_PIPELINE,
  approvalPaginationLabel,
  filterBucket,
  matchesApprovalSearch,
} from "../approvalQueue";
import {
  ApprovalDetails,
  ApprovalFilterBar,
  ApprovalRow,
  RejectDialog,
  type ApprovalReadState,
  type StatusFilter,
} from "../components";

type ActionKind = "approve" | "reject" | "start-review";

const APPROVAL_ACTION_MUTATION_KEY = ["platform", "approval-action"] as const;

type ApprovalActionVariables =
  | {
      readonly kind: "approve" | "start-review";
      readonly orgId: string;
      readonly reason?: never;
    }
  | {
      readonly kind: "reject";
      readonly orgId: string;
      readonly reason: string;
    };

type ActiveAction = Pick<ApprovalActionVariables, "kind" | "orgId">;

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState<StatusFilter>("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rejectDialogOrgId, setRejectDialogOrgId] = useState<string | null>(
    null,
  );
  const actionLock = useRef(false);
  const [activeAction, setActiveAction] = useState<ActiveAction | null>(null);
  const globalActionCount = useIsMutating({
    mutationKey: APPROVAL_ACTION_MUTATION_KEY,
  });
  const actionPending = activeAction !== null || globalActionCount > 0;

  const pipelineQuery = useQuery({
    queryKey: ["platform", "onboarding"],
    queryFn: listOnboardingOrgs,
  });

  const selectedOrgQuery = useQuery({
    queryKey: ["platform", "org", selectedOrgId],
    queryFn: () => getOrg(selectedOrgId!),
    enabled: selectedOrgId !== null,
  });

  const contractTermsQuery = useQuery({
    queryKey: ["platform", "contract-terms", selectedOrgId],
    queryFn: () => getContractTerms(selectedOrgId!),
    enabled: selectedOrgId !== null,
  });

  const actionMutation = useMutation({
    mutationKey: APPROVAL_ACTION_MUTATION_KEY,
    mutationFn: (variables: ApprovalActionVariables) => {
      switch (variables.kind) {
        case "approve":
          return approveOrg(variables.orgId);
        case "reject":
          return rejectOrg(variables.orgId, variables.reason);
        case "start-review":
          return startReview(variables.orgId);
      }
    },
    retry: false,
    onSuccess: async (_result, variables) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: ["platform", "onboarding"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "org", variables.orgId],
        }),
        queryClient.invalidateQueries({ queryKey: ["platform", "orgs"] }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "dashboard-stats"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "dashboard"],
        }),
        queryClient.invalidateQueries({ queryKey: ["platform", "audit"] }),
      ]);
    },
  });

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      200,
    );
    return () => clearTimeout(timer);
  }, [search]);

  const pipeline = pipelineQuery.data ?? EMPTY_APPROVAL_PIPELINE;
  const pipelineFailed =
    pipelineQuery.isError ||
    (pipelineQuery.isSuccess && pipelineQuery.data === undefined);
  const counts: Record<StatusFilter, number> = {
    All:
      pipeline.pending.length +
      pipeline.in_review.length +
      pipeline.approved.length,
    Pending: pipeline.pending.length,
    "In Review": pipeline.in_review.length,
    Approved: pipeline.approved.length,
  };

  const filtered = useMemo(
    () =>
      filterBucket(pipeline, activeFilter).filter((org) =>
        matchesApprovalSearch(org, debouncedSearch),
      ),
    [activeFilter, debouncedSearch, pipeline],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, debouncedSearch]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / APPROVAL_PAGE_SIZE),
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * APPROVAL_PAGE_SIZE;
  const pageEnd = Math.min(pageStart + APPROVAL_PAGE_SIZE, filtered.length);
  const paginatedOrgs = filtered.slice(pageStart, pageEnd);

  useEffect(() => {
    if (!actionPending && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [actionPending, currentPage, totalPages]);

  useEffect(() => {
    if (
      !actionPending &&
      selectedOrgId &&
      !paginatedOrgs.some((org) => org.org_id === selectedOrgId)
    ) {
      setSelectedOrgId(null);
    }
  }, [actionPending, paginatedOrgs, selectedOrgId]);

  useEffect(() => {
    if (rejectDialogOrgId && rejectDialogOrgId !== selectedOrgId) {
      setRejectDialogOrgId(null);
    }
  }, [rejectDialogOrgId, selectedOrgId]);

  function acquireAction(kind: ActionKind, orgId: string): boolean {
    if (
      actionLock.current ||
      queryClient.isMutating({ mutationKey: APPROVAL_ACTION_MUTATION_KEY }) > 0
    ) {
      return false;
    }
    actionLock.current = true;
    setActiveAction({ kind, orgId });
    return true;
  }

  function releaseAction() {
    actionLock.current = false;
    setActiveAction(null);
  }

  function currentReadyOrg(orgId: string): OrgResponse | null {
    const org = selectedOrgQuery.data;
    if (
      org?.org_id !== orgId ||
      contractTermsQuery.status !== "success" ||
      (org.status !== "pending" && org.status !== "in_review")
    ) {
      return null;
    }
    return org;
  }

  function beginStartReview(orgId: string) {
    const org = currentReadyOrg(orgId);
    if (!org || org.status !== "pending") return;
    if (!acquireAction("start-review", orgId)) return;

    actionMutation.mutate(
      { kind: "start-review", orgId },
      { onSettled: releaseAction },
    );
  }

  function beginApprove(orgId: string) {
    const org = currentReadyOrg(orgId);
    if (!org?.can_approve) return;
    if (!acquireAction("approve", orgId)) return;

    actionMutation.mutate(
      { kind: "approve", orgId },
      {
        onSuccess: (_result, variables) => {
          setSelectedOrgId((current) =>
            current === variables.orgId ? null : current,
          );
        },
        onSettled: releaseAction,
      },
    );
  }

  function openRejectDialog(orgId: string) {
    if (!currentReadyOrg(orgId) || actionLock.current) return;
    actionMutation.reset();
    setRejectDialogOrgId(orgId);
  }

  function beginReject(reason: string) {
    const orgId = rejectDialogOrgId;
    const trimmedReason = reason.trim();
    if (!orgId || !trimmedReason || trimmedReason.length > 1000) return;
    if (!currentReadyOrg(orgId) || !acquireAction("reject", orgId)) return;

    actionMutation.mutate(
      { kind: "reject", orgId, reason: trimmedReason },
      {
        onSuccess: (_result, variables) => {
          setSelectedOrgId((current) =>
            current === variables.orgId ? null : current,
          );
          setRejectDialogOrgId((current) =>
            current === variables.orgId ? null : current,
          );
        },
        onSettled: releaseAction,
      },
    );
  }

  const orgState: ApprovalReadState =
    selectedOrgQuery.isError ||
    (selectedOrgQuery.isSuccess &&
      selectedOrgQuery.data?.org_id !== selectedOrgId)
      ? "error"
      : selectedOrgQuery.isPending
        ? "loading"
        : "ready";
  const termsState: ApprovalReadState =
    contractTermsQuery.isError ||
    (contractTermsQuery.isSuccess && contractTermsQuery.data === undefined)
      ? "error"
      : contractTermsQuery.isPending
        ? "loading"
        : "ready";
  const failedAction = actionMutation.isError
    ? actionMutation.variables
    : undefined;
  const selectedActionError =
    failedAction?.orgId !== selectedOrgId
      ? null
      : failedAction.kind === "approve"
        ? "Launch approval could not be completed. Review the blockers and try again."
        : failedAction.kind === "start-review"
          ? "Review could not be started. Try again."
          : null;

  return (
    <PlatformPage
      title="Approvals"
      description="Review and decide on businesses applying to go live."
    >
      {pipelineFailed ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void pipelineQuery.refetch()}
            >
              Retry
            </Button>
          }
        >
          Approval applications could not be loaded. Try again.
        </Alert>
      ) : pipelineQuery.isPending ? (
        <Box
          role="status"
          aria-label="Loading approval applications"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={36} />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <ApprovalFilterBar
            search={search}
            activeFilter={activeFilter}
            counts={counts}
            disabled={actionPending}
            onSearchChange={(value) => {
              if (actionLock.current) return;
              setSearch(value);
              setCurrentPage(1);
            }}
            onFilterChange={(filter) => {
              if (actionLock.current) return;
              setActiveFilter(filter);
              setCurrentPage(1);
            }}
          />

          <Paper
            component="section"
            aria-labelledby="approval-queue-heading"
            variant="outlined"
            sx={{ overflow: "hidden" }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "baseline",
                justifyContent: "space-between",
                px: 2,
                py: 1.75,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                id="approval-queue-heading"
                component="h2"
                variant="h6"
                sx={{ fontWeight: 700 }}
              >
                Businesses
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {filtered.length} loaded{" "}
                {filtered.length === 1 ? "record" : "records"}
              </Typography>
            </Stack>

            {filtered.length === 0 ? (
              <Box
                role="status"
                aria-live="polite"
                sx={{ py: 6, textAlign: "center" }}
              >
                <Typography color="text.secondary">
                  No loaded applications match this view.
                </Typography>
              </Box>
            ) : (
              paginatedOrgs.map((org) => {
                const active = selectedOrgId === org.org_id;
                return (
                  <ApprovalRow
                    key={org.org_id}
                    org={org}
                    active={active}
                    disabled={actionPending}
                    onSelect={() => {
                      if (actionLock.current) return;
                      setSelectedOrgId((current) =>
                        current === org.org_id ? null : org.org_id,
                      );
                    }}
                  >
                    {active && (
                      <ApprovalDetails
                        orgId={org.org_id}
                        org={selectedOrgQuery.data}
                        terms={contractTermsQuery.data}
                        orgState={orgState}
                        termsState={termsState}
                        actionError={selectedActionError}
                        actionPending={actionPending}
                        approvePending={
                          activeAction?.kind === "approve" &&
                          activeAction.orgId === org.org_id
                        }
                        startReviewPending={
                          activeAction?.kind === "start-review" &&
                          activeAction.orgId === org.org_id
                        }
                        onRetryOrg={() => void selectedOrgQuery.refetch()}
                        onRetryTerms={() => void contractTermsQuery.refetch()}
                        onClose={() => {
                          if (!actionLock.current) setSelectedOrgId(null);
                        }}
                        onStartReview={() => beginStartReview(org.org_id)}
                        onApprove={() => beginApprove(org.org_id)}
                        onReject={() => openRejectDialog(org.org_id)}
                      />
                    )}
                  </ApprovalRow>
                );
              })
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{
                alignItems: { sm: "center" },
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography color="text.secondary" variant="body2">
                {filtered.length === 0
                  ? "Showing 0 of 0"
                  : `Showing ${pageStart + 1}-${pageEnd} of ${filtered.length}`}
              </Typography>
              <Pagination
                count={totalPages}
                page={safeCurrentPage}
                disabled={actionPending}
                size="small"
                showFirstButton
                showLastButton
                getItemAriaLabel={approvalPaginationLabel}
                onChange={(_event, page) => {
                  if (!actionLock.current) setCurrentPage(page);
                }}
              />
            </Stack>
          </Paper>
        </Stack>
      )}

      <RejectDialog
        open={rejectDialogOrgId !== null}
        pending={
          activeAction?.kind === "reject" &&
          activeAction.orgId === rejectDialogOrgId
        }
        failed={
          failedAction?.kind === "reject" &&
          failedAction.orgId === rejectDialogOrgId
        }
        onCancel={() => {
          if (!actionLock.current) setRejectDialogOrgId(null);
        }}
        onConfirm={beginReject}
      />
    </PlatformPage>
  );
}
